import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Cookie, Database, Lock, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="privacy-policy-page">
      <div className="privacy-policy-container">
        <motion.div
          className="privacy-policy-card"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div className="privacy-policy-header" variants={itemVariants}>
            <Link to="/login" className="back-button">
              <ArrowLeft size={20} />
              Back to Login
            </Link>
            
            <div className="privacy-policy-title">
              <Shield size={32} className="privacy-icon" />
              <h1>Privacy Policy</h1>
              <p className="privacy-subtitle">
                CLIA Laboratory Analytics Dashboard
              </p>
            </div>
            
            <div className="privacy-policy-meta">
              <p><strong>Last Updated:</strong> {new Date().toLocaleDateString()}</p>
              <p><strong>Effective Date:</strong> {new Date().toLocaleDateString()}</p>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div className="privacy-policy-content" variants={itemVariants}>
            
            {/* Introduction */}
            <section className="privacy-section">
              <h2>Introduction</h2>
              <p>
                This Privacy Policy describes how CLIA Laboratory Analytics Dashboard ("we," "our," or "us") 
                collects, uses, and protects your personal information when you use our laboratory analytics platform.
              </p>
              <p>
                This application is designed for internal laboratory use and complies with healthcare data 
                protection standards including HIPAA (Health Insurance Portability and Accountability Act).
              </p>
            </section>

            {/* Information We Collect */}
            <section className="privacy-section">
              <h2>
                <Database size={20} />
                Information We Collect
              </h2>
              <div className="info-grid">
                <div className="info-item">
                  <h3>Authentication Information</h3>
                  <ul>
                    <li>Email address</li>
                    <li>Password (encrypted)</li>
                    <li>User role and permissions</li>
                    <li>Login timestamps</li>
                  </ul>
                </div>
                
                <div className="info-item">
                  <h3>Laboratory Data</h3>
                  <ul>
                    <li>Laboratory metrics and reports</li>
                    <li>Quality control data</li>
                    <li>Compliance metrics</li>
                    <li>Analytical results</li>
                  </ul>
                </div>
                
                <div className="info-item">
                  <h3>Technical Information</h3>
                  <ul>
                    <li>IP address</li>
                    <li>Browser type and version</li>
                    <li>Device information</li>
                    <li>Session data</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Cookie Usage */}
            <section className="privacy-section">
              <h2>
                <Cookie size={20} />
                Cookie Usage
              </h2>
              <p>
                We use essential cookies to provide secure authentication and maintain your session. 
                These cookies are necessary for the application to function properly.
              </p>
              
              <div className="cookie-table">
                <div className="cookie-header">
                  <span>Cookie Name</span>
                  <span>Purpose</span>
                  <span>Duration</span>
                  <span>Essential</span>
                </div>
                
                <div className="cookie-row">
                  <span>authToken</span>
                  <span>User authentication</span>
                  <span>1 day</span>
                  <span className="essential-badge">Yes</span>
                </div>
                
                <div className="cookie-row">
                  <span>refreshToken</span>
                  <span>Token refresh</span>
                  <span>7 days</span>
                  <span className="essential-badge">Yes</span>
                </div>
                
                <div className="cookie-row">
                  <span>userInfo</span>
                  <span>User preferences</span>
                  <span>1 day</span>
                  <span className="essential-badge">Yes</span>
                </div>
              </div>
              
              <div className="cookie-note">
                <p>
                  <strong>Note:</strong> All cookies used are essential for the application's security and functionality. 
                  No tracking, analytics, or advertising cookies are used.
                </p>
              </div>
            </section>

            {/* Data Security */}
            <section className="privacy-section">
              <h2>
                <Lock size={20} />
                Data Security
              </h2>
              <div className="security-features">
                <div className="security-item">
                  <Lock size={16} />
                  <span>End-to-end encryption for all data transmission</span>
                </div>
                <div className="security-item">
                  <Lock size={16} />
                  <span>Secure token-based authentication (JWT)</span>
                </div>
                <div className="security-item">
                  <Lock size={16} />
                  <span>Regular security audits and updates</span>
                </div>
                <div className="security-item">
                  <Lock size={16} />
                  <span>Access controls and role-based permissions</span>
                </div>
                <div className="security-item">
                  <Lock size={16} />
                  <span>Compliance with HIPAA and healthcare standards</span>
                </div>
              </div>
            </section>

            {/* Your Rights */}
            <section className="privacy-section">
              <h2>
                <Eye size={20} />
                Your Rights
              </h2>
              <div className="rights-grid">
                <div className="right-item">
                  <h3>Access</h3>
                  <p>Request access to your personal data</p>
                </div>
                <div className="right-item">
                  <h3>Correction</h3>
                  <p>Request correction of inaccurate data</p>
                </div>
                <div className="right-item">
                  <h3>Deletion</h3>
                  <p>Request deletion of your data</p>
                </div>
                <div className="right-item">
                  <h3>Portability</h3>
                  <p>Request data in a portable format</p>
                </div>
              </div>
            </section>

            {/* Contact Information */}
            <section className="privacy-section">
              <h2>Contact Information</h2>
              <div className="contact-info">
                <p>
                  If you have questions about this Privacy Policy or our data practices, 
                  please contact your laboratory administrator or IT department.
                </p>
                <div className="contact-details">
                  <p><strong>Application:</strong> CLIA Laboratory Analytics Dashboard</p>
                  <p><strong>Purpose:</strong> Internal laboratory compliance and quality metrics</p>
                  <p><strong>Data Controller:</strong> Your Laboratory Organization</p>
                  <p><strong>Software Provider:</strong> Clinical Squared®</p>
                </div>
              </div>
            </section>

            {/* Updates */}
            <section className="privacy-section">
              <h2>Policy Updates</h2>
              <p>
                We may update this Privacy Policy from time to time. Any changes will be posted 
                on this page with an updated revision date. Continued use of the application 
                after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Clinical Squared Branding */}
            <section className="privacy-section">
              <div className="clinical-squared-branding">
                <p className="powered-by">
                  This application is developed and maintained by{' '}
                  <a 
                    href="https://clinicalsquared.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="clinical-squared-link"
                  >
                    Clinical Squared
                  </a>
                  <span className="trademark">®</span>
                </p>
                <p className="company-description">
                  A multi-disciplined technology consulting company specializing in custom software development and data analytics for healthcare organizations.
                </p>
              </div>
            </section>

          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
